import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { adminApi, type AdminNotificationRow } from '../api'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { Button } from '../../components/ui/Button'
import { safeAdminError } from '../safe-ui'

const AUDIENCES: Array<{ value: string; label: string }> = [
  { value: 'clients_active', label: 'Active clients' },
  { value: 'clients_all', label: 'All clients' },
  { value: 'ib_active', label: 'Active IB / sub-admins' },
  { value: 'ib_all', label: 'All IB / sub-admins' },
  { value: 'all_active', label: 'Active clients + active IB' },
  { value: 'all', label: 'Everyone (clients + IB)' },
]

const CHANNELS: Array<{ value: string; label: string }> = [
  { value: 'web', label: 'Web (in-app inbox)' },
  { value: 'email', label: 'Email only' },
  { value: 'both', label: 'Web + email' },
]

export function AdminNotificationsPage() {
  const [audience, setAudience] = useState('clients_active')
  const [channel, setChannel] = useState('web')
  const [subject, setSubject] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [ctaLabel, setCtaLabel] = useState('')
  const [ctaPath, setCtaPath] = useState('')
  const [preview, setPreview] = useState<{
    recipientCount: number
    clientCount: number
    ibCount: number
    smtpConfigured: boolean
    capped: boolean
  } | null>(null)
  const [history, setHistory] = useState<AdminNotificationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const needsSmtp = channel === 'email' || channel === 'both'

  const refresh = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [p, h] = await Promise.all([
        adminApi.notificationsPreview(audience),
        adminApi.notificationsHistory(30),
      ])
      setPreview({
        recipientCount: p.recipientCount,
        clientCount: p.clientCount,
        ibCount: p.ibCount,
        smtpConfigured: p.smtpConfigured,
        capped: p.capped,
      })
      setHistory(h.notifications || [])
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load notifications'))
    } finally {
      setLoading(false)
    }
  }, [audience])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function onSend(e: FormEvent) {
    e.preventDefault()
    const channelLabel = CHANNELS.find((c) => c.value === channel)?.label || channel
    if (
      !window.confirm(
        `Send via ${channelLabel} to ${preview?.recipientCount ?? 0} recipient(s)? This cannot be undone.`,
      )
    ) {
      return
    }
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const res = await adminApi.notificationsSend({
        audience,
        channel,
        subject: subject.trim(),
        title: title.trim(),
        body: body.trim(),
        ctaLabel: ctaLabel.trim() || undefined,
        ctaPath: ctaPath.trim() || undefined,
      })
      const n = res.notification
      setNotice(
        `Notification #${n.id} finished: ${n.sentCount} delivered, ${n.skippedCount} skipped, ${n.failedCount} failed.`,
      )
      setSubject('')
      setTitle('')
      setBody('')
      await refresh()
    } catch (err) {
      setError(safeAdminError(err, 'Unable to send notification'))
    } finally {
      setBusy(false)
    }
  }

  const canSend =
    !busy &&
    (preview?.recipientCount ?? 0) > 0 &&
    (!needsSmtp || Boolean(preview?.smtpConfigured))

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Notifications">
        <p className="cp-muted">
          Send in-app (web) notifications to the client / IB portal inbox. Optionally also email.
        </p>
      </PageHeader>
      {error ? <StateBlock tone="error">{error}</StateBlock> : null}
      {notice ? <StateBlock tone="info">{notice}</StateBlock> : null}
      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}

      <form className="cp-panel cp-auth__form" onSubmit={(e) => void onSend(e)} style={{ marginTop: '1rem' }}>
        <label className="cp-field">
          <span className="cp-field__label">Audience</span>
          <select
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            disabled={busy}
          >
            {AUDIENCES.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </label>

        <label className="cp-field">
          <span className="cp-field__label">Channel</span>
          <select value={channel} onChange={(e) => setChannel(e.target.value)} disabled={busy}>
            {CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        {preview ? (
          <p className="cp-muted" style={{ marginTop: 0 }}>
            Recipients: <strong>{preview.recipientCount}</strong>
            {` (${preview.clientCount} clients · ${preview.ibCount} IB)`}
            {needsSmtp && !preview.smtpConfigured ? ' · SMTP not configured' : ''}
            {preview.capped ? ' · list capped at max recipients' : ''}
          </p>
        ) : loading ? (
          <p className="cp-muted">Loading recipient counts…</p>
        ) : null}
        {preview && preview.recipientCount < 1 ? (
          <StateBlock tone="warn">No recipients match this audience. Adjust the audience before sending.</StateBlock>
        ) : null}
        {preview && needsSmtp && !preview.smtpConfigured ? (
          <StateBlock tone="warn">
            SMTP is not configured on the server. Choose Web channel, or configure SMTP for email.
          </StateBlock>
        ) : null}

        <label className="cp-field">
          <span className="cp-field__label">{needsSmtp ? 'Email subject' : 'Subject (for history)'}</span>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={160}
            required
            disabled={busy}
            placeholder="Market hours update"
          />
        </label>

        <label className="cp-field">
          <span className="cp-field__label">Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            required
            disabled={busy}
            placeholder="Important update"
          />
        </label>

        <label className="cp-field">
          <span className="cp-field__label">Message body</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
            required
            disabled={busy}
            rows={8}
            placeholder="Write the message clients / IB will see in their portal…"
          />
        </label>

        <label className="cp-field">
          <span className="cp-field__label">CTA button label (optional)</span>
          <input
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
            maxLength={40}
            disabled={busy}
            placeholder="Open Portal"
          />
        </label>

        <label className="cp-field">
          <span className="cp-field__label">CTA path (optional)</span>
          <input
            value={ctaPath}
            onChange={(e) => setCtaPath(e.target.value)}
            maxLength={200}
            disabled={busy}
            placeholder="/app/deposits or /ib/wallet"
          />
        </label>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          <Button
            type="submit"
            disabled={!canSend}
            title={
              !canSend
                ? needsSmtp && !preview?.smtpConfigured
                  ? 'SMTP not configured'
                  : (preview?.recipientCount ?? 0) < 1
                    ? 'No recipients'
                    : 'Complete the form to send'
                : undefined
            }
          >
            {busy ? 'Sending…' : channel === 'email' ? 'Send email' : 'Send notification'}
          </Button>
          <Button type="button" variant="secondary" disabled={busy} onClick={() => void refresh()}>
            Refresh counts
          </Button>
        </div>
      </form>

      <h2 style={{ marginTop: '2rem', fontSize: '1.1rem' }}>Recent sends</h2>
      <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
        <ResponsiveTable
          rowKey={(r) => String(r.id)}
        columns={[
          { key: 'id', header: 'ID', render: (r) => String(r.id) },
          { key: 'createdAt', header: 'When', render: (r) => String(r.createdAt || '—') },
          { key: 'channel', header: 'Channel', render: (r) => String(r.channel || '—') },
          { key: 'audience', header: 'Audience', render: (r) => String(r.audience) },
          { key: 'subject', header: 'Subject', render: (r) => String(r.subject) },
          {
            key: 'counts',
            header: 'Sent / skip / fail',
            render: (r) => `${r.sentCount} / ${r.skippedCount} / ${r.failedCount}`,
          },
          { key: 'status', header: 'Status', render: (r) => String(r.status) },
          {
            key: 'error',
            header: 'Errors',
            render: (r) => (r.errorSummary ? String(r.errorSummary) : '—'),
          },
        ]}
        rows={history}
        empty={loading ? 'Loading…' : 'No notifications sent yet.'}
      />
      </div>
    </div>
  )
}
